// src/components/notifications/NotificationTypeGuide.jsx
import React, { memo, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Assignment,
  Schedule,
  Build,
  SystemUpdate,
  HelpOutline,
} from '@mui/icons-material';

const NotificationTypeGuide = memo(function NotificationTypeGuide() {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const NOTIFICATION_GUIDE = useMemo(() => [
    {
      type: 'info',
      label: t('notifications:notification_types.info'),
      color: 'info',
      icon: <Info fontSize="small" />,
      description: t('notifications:type_guide.info.description'),
      examples: [
        t('notifications:type_guide.info.example_1'),
        t('notifications:type_guide.info.example_2'),
        t('notifications:type_guide.info.example_3'),
      ],
    },
    {
      type: 'warning',
      label: t('notifications:notification_types.warning'),
      color: 'warning',
      icon: <Warning fontSize="small" />,
      description: t('notifications:type_guide.warning.description'),
      examples: [
        t('notifications:type_guide.warning.example_1'),
        t('notifications:type_guide.warning.example_2'),
        t('notifications:type_guide.warning.example_3'),
      ],
    },
    {
      type: 'error',
      label: t('notifications:notification_types.error'),
      color: 'error',
      icon: <ErrorIcon fontSize="small" />,
      description: t('notifications:type_guide.error.description'),
      examples: [
        t('notifications:type_guide.error.example_1'),
        t('notifications:type_guide.error.example_2'),
        t('notifications:type_guide.error.example_3'),
      ],
    },
    {
      type: 'success',
      label: t('notifications:notification_types.success'),
      color: 'success',
      icon: <CheckCircle fontSize="small" />,
      description: t('notifications:type_guide.success.description'),
      examples: [
        t('notifications:type_guide.success.example_1'),
        t('notifications:type_guide.success.example_2'),
        t('notifications:type_guide.success.example_3'),
      ],
    },
    {
      type: 'task_assigned',
      label: t('notifications:notification_types.task_assigned'),
      color: 'primary',
      icon: <Assignment fontSize="small" />,
      description: t('notifications:type_guide.task_assigned.description'),
      examples: [
        t('notifications:type_guide.task_assigned.example_1'),
        t('notifications:type_guide.task_assigned.example_2'),
        t('notifications:type_guide.task_assigned.example_3'),
      ],
    },
    {
      type: 'task_completed',
      label: t('notifications:notification_types.task_completed'),
      color: 'success',
      icon: <CheckCircle fontSize="small" />,
      description: t('notifications:type_guide.task_completed.description'),
      examples: [
        t('notifications:type_guide.task_completed.example_1'),
        t('notifications:type_guide.task_completed.example_2'),
        t('notifications:type_guide.task_completed.example_3'),
      ],
    },
    {
      type: 'deadline_reminder',
      label: t('notifications:notification_types.deadline_reminder'),
      color: 'warning',
      icon: <Schedule fontSize="small" />,
      description: t('notifications:type_guide.deadline_reminder.description'),
      examples: [
        t('notifications:type_guide.deadline_reminder.example_1'),
        t('notifications:type_guide.deadline_reminder.example_2'),
        t('notifications:type_guide.deadline_reminder.example_3'),
      ],
    },
    {
      type: 'system_update',
      label: t('notifications:notification_types.system_update'),
      color: 'info',
      icon: <SystemUpdate fontSize="small" />,
      description: t('notifications:type_guide.system_update.description'),
      examples: [
        t('notifications:type_guide.system_update.example_1'),
        t('notifications:type_guide.system_update.example_2'),
        t('notifications:type_guide.system_update.example_3'),
      ],
    },
    {
      type: 'maintenance',
      label: t('notifications:notification_types.maintenance'),
      color: 'warning',
      icon: <Build fontSize="small" />,
      description: t('notifications:type_guide.maintenance.description'),
      examples: [
        t('notifications:type_guide.maintenance.example_1'),
        t('notifications:type_guide.maintenance.example_2'),
        t('notifications:type_guide.maintenance.example_3'),
      ],
    },
  ], [t]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleAccordionChange = useCallback((panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  }, []);

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">
            {t('notifications:type_guide.title')}
          </Typography>
          <IconButton 
            size="small" 
            onClick={toggleExpanded}
            sx={{ 
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease-in-out'
            }}
          >
            <HelpOutline fontSize="small" />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          {t('notifications:type_guide.subtitle')}
        </Typography>

        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2 }}>
            <Stack spacing={1}>
              {NOTIFICATION_GUIDE.map((guide) => (
                <Accordion 
                  key={guide.type} 
                  elevation={0} 
                  sx={{ border: 1, borderColor: 'divider' }}
                  expanded={expandedAccordion === guide.type}
                  onChange={handleAccordionChange(guide.type)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={1} width="100%">
                      {guide.icon}
                      <Chip
                        label={guide.label}
                        size="small"
                        color={guide.color}
                        variant="outlined"
                        sx={{ pointerEvents: 'none' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" paragraph>
                      {guide.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                      {t('notifications:type_guide.examples_label')}
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                      {guide.examples.map((example, index) => (
                        <Typography key={index} component="li" variant="caption" color="text.secondary">
                          {example}
                        </Typography>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Box>
        </Collapse>

        {!isExpanded && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('notifications:type_guide.help_text')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});

export default NotificationTypeGuide;